// ── BCHNostr — active sponsored business ads ──────────────────────
//
// Loads the currently-eligible sponsored ads for the feed. An ad is only
// eligible when ALL of the following hold (see lib/businessAd.ts for why):
//
//   1. Signed by the business it promotes  (parseBusinessAd enforces
//      businessPubkey === author).
//   2. Not expired.
//   3. The business is CURRENTLY VERIFIED via an issuer-signed grant.
//   4. Its payment txid is confirmed on-chain to the platform wallet.
//
// Verification (3) is author-filtered to the trusted issuer set — that is the
// trust boundary, exactly as the merchant directory / video verification do.
// On-chain payment (4) reuses verifyBoostPayment with a short grace window
// for fresh/unconfirmed txs, mirroring useVerifiedGoalBoosts.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNostrQuery } from './useNostrQuery';
import {
  BUSINESS_AD_KIND, BUSINESS_AD_TAG,
  parseBusinessAd, isAdActive, verifyBoostPayment,
  type BusinessAdData,
} from '../lib/businessAd';
import {
  BUSINESS_VERIFY_KIND, BUSINESS_VERIFY_GRANT_TAG, VERIFY_GRANT_ISSUERS,
  newestGrantsByBusiness, computeVerificationState,
} from '../lib/businessVerify';

export interface ActiveBusinessAd {
  /** The raw event id (for React keys / dedup). */
  id: string;
  /** The promoted business pubkey (== event author). */
  businessPubkey: string;
  data: BusinessAdData;
}

/** How long a paid-but-unconfirmed ad is trusted before we require on-chain. */
const PAYMENT_GRACE_MS = 20 * 60 * 1000; // 20 minutes

/**
 * Active, verified, on-chain-confirmed sponsored ads, ranked by amount paid
 * (highest first) and capped. One ad per business (newest wins).
 */
export function useBusinessAds(max = 3): { ads: ActiveBusinessAd[]; loading: boolean } {
  // Look back far enough to catch a 30-day ad; relays may also honour the
  // NIP-40 `expiration` tag and prune early, which is fine.
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 31 * 86400);

  // All ad events (broad, indexable tag query across all relays).
  const { events: adEvents, loading: adsLoading } = useNostrQuery({
    filters: [{ kinds: [BUSINESS_AD_KIND], '#t': [BUSINESS_AD_TAG], since, limit: 200 }],
    allRelays: true,
  });

  // Collapse to the newest active, self-consistent ad per business.
  const candidateAds = useMemo(() => {
    const byBiz = new Map<string, { ev: { id: string; created_at: number }; data: BusinessAdData }>();
    for (const ev of adEvents) {
      const data = parseBusinessAd(ev);
      if (!data || !isAdActive(data)) continue;
      const existing = byBiz.get(data.businessPubkey);
      if (!existing || ev.created_at > existing.ev.created_at) {
        byBiz.set(data.businessPubkey, { ev: { id: ev.id, created_at: ev.created_at }, data });
      }
    }
    return [...byBiz.values()];
  }, [adEvents]);

  const candidateBizPubkeys = useMemo(
    () => candidateAds.map(c => c.data.businessPubkey),
    [candidateAds],
  );

  // Verification grants for exactly those businesses (author-filtered to the
  // trusted issuer set — the trust boundary for "verified").
  const { events: grantEvents, loading: grantsLoading } = useNostrQuery({
    filters: candidateBizPubkeys.length > 0
      ? [{
          kinds: [BUSINESS_VERIFY_KIND],
          authors: VERIFY_GRANT_ISSUERS,
          '#t': [BUSINESS_VERIFY_GRANT_TAG],
          '#p': candidateBizPubkeys,
          limit: 500,
        }]
      : [],
    enabled: candidateBizPubkeys.length > 0,
    allRelays: true,
  });

  const verifiedBizPubkeys = useMemo(() => {
    const grants = newestGrantsByBusiness(grantEvents);
    const verified = new Set<string>();
    for (const [biz, grant] of grants) {
      if (computeVerificationState(grant, null).status === 'VERIFIED') verified.add(biz);
    }
    return verified;
  }, [grantEvents]);

  // Ads whose business is verified — the only ones we bother to on-chain check.
  const verifiedAds = useMemo(
    () => candidateAds.filter(c => verifiedBizPubkeys.has(c.data.businessPubkey)),
    [candidateAds, verifiedBizPubkeys],
  );

  // On-chain payment confirmation. Cached per txid so we don't re-hit
  // explorers on every render. A fresh ad (within the grace window) is shown
  // optimistically even before the tx confirms, then re-checked.
  const [paidTxids, setPaidTxids] = useState<Set<string>>(new Set());
  const checkedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const c of verifiedAds) {
        const { txid, priceSats } = c.data;
        if (checkedRef.current.has(txid)) continue;
        checkedRef.current.add(txid);
        const verdict = await verifyBoostPayment(txid, priceSats);
        if (cancelled) return;
        if (verdict === 'paid') {
          setPaidTxids(prev => (prev.has(txid) ? prev : new Set(prev).add(txid)));
        } else if (verdict === 'not-found' || verdict === 'unavailable') {
          // Transient — allow a later re-check.
          checkedRef.current.delete(txid);
        }
        // 'unpaid' → leave unconfirmed permanently (fabricated/underpaid).
      }
    })();
    return () => { cancelled = true; };
  }, [verifiedAds]);

  const ads = useMemo(() => {
    const now = Date.now();
    const eligible = verifiedAds.filter(c => {
      if (paidTxids.has(c.data.txid)) return true;
      // Grace window: a brand-new ad shows while its tx is still confirming.
      const ageMs = now - c.ev.created_at * 1000;
      return ageMs >= 0 && ageMs < PAYMENT_GRACE_MS;
    });
    return eligible
      .sort((a, b) => b.data.priceSats - a.data.priceSats)
      .slice(0, max)
      .map(c => ({ id: c.ev.id, businessPubkey: c.data.businessPubkey, data: c.data }));
  }, [verifiedAds, paidTxids, max]);

  return { ads, loading: adsLoading || grantsLoading };
}
